/**
 * tailClamp - Text clamping utility with expand/collapse functionality
 *
 * @param {string|Element} root - Selector string or DOM element containing the clamp structure
 * @param {Object} opts - Configuration options
 * @param {string} opts.clampClass - CSS class for text clamping (e.g., 'line-clamp-1')
 * @param {boolean} opts.forceToggle - Keep the toggle visible even when text fits
 * @param {boolean} opts.expandOnly - Keep content expanded after opening
 * @param {Function} opts.onToggle - Callback when expand/collapse state changes
 * @param {Function} opts.onOverflowChange - Callback when overflow state changes
 * @param {HTMLElement} template[data-clamp-deferred-template] - Optional content inserted on first expansion
 * @returns {Object} API object with update, expand, collapse, destroy methods
 */
const DUMMY = { update(){}, expand(){}, collapse(){}, destroy(){} };
const pendingMeasurements = new Set();
let batchDepth = 0;
let measurementFrame = null;

function flushMeasurements() {
  if (measurementFrame !== null) cancelAnimationFrame(measurementFrame);
  measurementFrame = null;
  const measurements = Array.from(pendingMeasurements).filter((measurement) => measurement.active());
  pendingMeasurements.clear();

  // 全文を折りたたんでからまとめて測り、表示の書き換えを読取りの間に挟まない。
  measurements.forEach((measurement) => measurement.prepare());
  const results = measurements.map((measurement) => measurement.read());
  measurements.forEach((measurement) => measurement.restore());
  measurements.forEach((measurement, index) => measurement.apply(results[index]));
}

export function batchTailClamps(callback) {
  batchDepth++;
  try {
    return callback();
  } finally {
    batchDepth--;
    if (batchDepth === 0) flushMeasurements();
  }
}

export function tailClamp(root, opts = {}) {
  const el = typeof root === 'string' ? document.querySelector(root) : root;
  if (!el) return DUMMY;

  const text = el.querySelector('[data-clamp-text]');
  const btn = el.querySelector('[data-clamp-toggle]');
  if (!text) return DUMMY;

  const clampClass = opts.clampClass;
  let wasClamped = text.classList.contains(clampClass);
  text.classList.add(clampClass);
  const hiddenClass = 'hidden';
  const forceToggle = opts.forceToggle === true;

  let expanded = false;
  let hasBeenOpened = false;
  let initialized = false;
  let destroyed = false;
  const needsOverflowMeasurement = () => !opts.expandOnly || !hasBeenOpened || Boolean(opts.onOverflowChange);

  const loadDeferredTemplates = () => {
    el.querySelectorAll('template[data-clamp-deferred-template]').forEach((template) => {
      if (!(template instanceof HTMLTemplateElement) || !template.parentElement) return;

      template.parentElement.insertBefore(template.content.cloneNode(true), template);
      template.remove();
    });
  };

  const apply = () => {
    if (expanded) loadDeferredTemplates();
    text.classList.toggle(clampClass, !expanded);
    if (btn) {
      btn.setAttribute('aria-expanded', String(expanded));
      if (opts.expandOnly && expanded) btn.classList.add(hiddenClass);
    }
    if (opts.onToggle) opts.onToggle(expanded, { root: el, text, btn });
    if (!needsOverflowMeasurement()) {
      ro.disconnect();
      mo.disconnect();
    }
  };

  const setOverflowUI = (needs) => {
    if (btn) btn.classList.toggle(hiddenClass, !needs || Boolean(opts.expandOnly && expanded));
    el.dataset.overflow = needs ? 'true' : 'false';
    if (opts.onOverflowChange) opts.onOverflowChange(needs, { root: el, text, btn });
  };

  const onClick = () => {
    hasBeenOpened = true;
    expanded = opts.expandOnly || !expanded;
    apply();
    if (opts.expandOnly && document.activeElement === btn) {
      text.focus({ preventScroll: true });
    }
  };
  if (btn) btn.addEventListener('click', onClick);

  const measurement = {
    active: () => !destroyed && needsOverflowMeasurement(),
    prepare() {
      if (initialized) wasClamped = text.classList.contains(clampClass);
      text.classList.add(clampClass);
    },
    read: () => forceToggle || text.scrollHeight > text.clientHeight + 1,
    restore() {
      text.classList.toggle(clampClass, wasClamped);
    },
    apply(needsToggle) {
      if (!initialized) {
        initialized = true;
        expanded = !needsToggle;
        setOverflowUI(needsToggle);
        apply();
        return;
      }
      if (opts.expandOnly && !hasBeenOpened) {
        expanded = !needsToggle;
        apply();
      }
      setOverflowUI(needsToggle);
    },
  };

  const update = () => {
    if (!measurement.active()) return;
    pendingMeasurements.add(measurement);
    if (batchDepth === 0) flushMeasurements();
  };
  const scheduleUpdate = () => {
    if (!measurement.active()) return;
    pendingMeasurements.add(measurement);
    if (measurementFrame === null) measurementFrame = requestAnimationFrame(flushMeasurements);
  };

  const ro = new ResizeObserver(scheduleUpdate);
  ro.observe(text);
  const mo = new MutationObserver(scheduleUpdate);
  mo.observe(text, { subtree: true, childList: true, characterData: true });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleUpdate);
  }
  update();

  return {
    update,
    expand(){ hasBeenOpened = true; expanded = true; apply(); },
    collapse(){ if (!opts.expandOnly) { expanded = false; apply(); } },
    destroy(){
      destroyed = true;
      pendingMeasurements.delete(measurement);
      ro.disconnect();
      mo.disconnect();
      if (btn) btn.removeEventListener('click', onClick);
    },
  };
}
